import React, { useState, useEffect, useCallback } from "react"
import { ArrowRightIcon, AlertCircle, CheckCircle, Sparkles } from "lucide-react"
import { 
  TextField, 
  Button, 
  Box, 
  Typography, 
  Alert, 
  AlertTitle,
  FormControl,
  FormLabel,
  CircularProgress,
  IconButton,
  Tooltip,
  LinearProgress
} from "@mui/material"
import { History as HistoryIcon } from "@mui/icons-material"
import { snowflakeApi } from "../../utils/api"
import ModelSelectorButton from "./ModelSelectorButton"
import TestRunButton from "./TestRunButton"
import PromptEditorV2 from "./PromptEditorV2"

interface InputFormContainerV2Props {
  className?: string
  onAnalysisComplete?: (results: any) => void
  isSubmitting?: boolean
  workflowId?: string | null
  workflow?: any
}

interface FormData {
  businessName: string
  brandTokens: string
  industry: string
  description: string
  targetAudience: string
  additionalContext: string
  customPrompt: string
  keywordLimit?: number
}

const InputFormContainerV2: React.FC<InputFormContainerV2Props> = ({ 
  className, 
  onAnalysisComplete, 
  isSubmitting = false,
  workflowId = null,
  workflow = null
}) => {
  const [formData, setFormData] = useState<FormData>({
    businessName: "",
    brandTokens: "",
    industry: "",
    description: "",
    targetAudience: "",
    additionalContext: "",
    customPrompt: "",
    keywordLimit: undefined
  })

  const [missingFields, setMissingFields] = useState<string[]>([])
  const [isComplete, setIsComplete] = useState(false)
  const [isLoadingConfig, setIsLoadingConfig] = useState(false)
  const [configLoaded, setConfigLoaded] = useState(false)
  const [isGeneratingBrandTokens, setIsGeneratingBrandTokens] = useState(false)
  
  // LLM Model selection
  const [selectedModel, setSelectedModel] = useState<string>("")

  // Memoized callback for prompt changes
  const handlePromptChange = useCallback((prompt: string) => {
    setFormData(prev => ({
      ...prev,
      customPrompt: prompt || ""
    }));
  }, []);

  // Reset form data when workflowId changes
  useEffect(() => {
    console.log(`InputFormContainerV2: workflowId changed to ${workflowId}, resetting form`)
    setFormData({
      businessName: "",
      brandTokens: "",
      industry: "",
      description: "",
      targetAudience: "",
      additionalContext: "",
      customPrompt: "",
      keywordLimit: undefined
    })
    setConfigLoaded(false)
  }, [workflowId])

  // Load saved business configuration
  useEffect(() => {
    const loadBusinessConfig = async () => {
      console.log(`InputFormContainerV2: Loading business config for workflowId=${workflowId}`)
      
      if (!workflow?.workflow_id) {
        console.log('InputFormContainerV2: No workflow.workflow_id available')
        return
      }
      
      // Ensure workflow matches current workflowId
      if (workflowId && workflow?.workflow_id?.toString() !== workflowId.toString()) {
        console.log(`InputFormContainerV2: workflow_id mismatch, skipping load`)
        return
      }
      
      try {
        setIsLoadingConfig(true)
        const response = await snowflakeApi.get(
          `/api/v1/db/workflow/${workflow.workflow_id}/business-config`
        )
        
        if (response.data.business_config && Object.keys(response.data.business_config).length > 0) {
          const config = response.data.business_config
          
          console.log('InputFormContainerV2: Found business config, populating form', config)
          setFormData({
            businessName: config.business_name || '',
            brandTokens: config.brand_tokens || '',
            industry: config.industry || '',
            description: config.description || '',
            targetAudience: config.target_audience || '',
            additionalContext: config.additional_context || '',
            customPrompt: "",
            keywordLimit: config.keyword_limit || undefined
          })
          
          setConfigLoaded(true)
        }
      } catch (error) {
        console.log('No previous business config found or error loading:', error)
      } finally {
        setIsLoadingConfig(false)
      }
    }
    
    loadBusinessConfig()
  }, [workflowId, workflow?.workflow_id])

  // Check if form data is complete
  useEffect(() => {
    const missing = []
    if (!formData.businessName.trim()) missing.push("Business name")
    if (!formData.description.trim()) missing.push("Business description")
    
    setMissingFields(missing)
    setIsComplete(missing.length === 0)
  }, [formData])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async () => {
    if (!isComplete || isSubmitting) return

    const submissionData = {
      ...formData,
      llmModel: selectedModel,
      customPrompt: formData.customPrompt
    }
    
    console.log("Form data submitted:", submissionData)
    
    if (onAnalysisComplete) {
      onAnalysisComplete(submissionData)
    }
  }
  
  const handleModelSelect = (modelValue: string) => {
    setSelectedModel(modelValue)
  }
  
  const handleTestRun = (keywordLimit: number) => {
    // Trigger submission with the selected limit
    if (onAnalysisComplete) {
      const submissionData = {
        ...formData,
        keywordLimit: keywordLimit,
        llmModel: selectedModel,
        customPrompt: formData.customPrompt
      }
      onAnalysisComplete(submissionData)
    }
  }

  const handleGenerateBrandTokens = async () => {
    if (!formData.businessName.trim()) {
      alert("Please enter a business name first")
      return
    }

    setIsGeneratingBrandTokens(true)
    try {
      const response = await snowflakeApi.post(
        "/api/v1/workflow-v2/generate-brand-tokens",
        {
          business_name: formData.businessName,
          industry: formData.industry || null,
          description: formData.description || null,
          target_audience: formData.targetAudience || null,
          additional_context: formData.additionalContext || null
        }
      )

      if (response.data.success && response.data.brand_tokens) {
        setFormData(prev => ({
          ...prev,
          brandTokens: response.data.brand_tokens
        }))
      }
    } catch (error: any) {
      console.error("Error generating brand tokens:", error)
      alert(
        error.response?.data?.detail || 
        "Failed to generate brand tokens. Please try again."
      )
    } finally {
      setIsGeneratingBrandTokens(false)
    }
  }

  return (
    <Box 
      className={className}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        height: '100%',
        overflow: 'auto'
      }}
    >
        {/* Loading indicator */}
        {isLoadingConfig && (
          <Alert severity="info" icon={<CircularProgress size={20} />}>
            Loading previous business information...
          </Alert>
        )}
        
        {/* Data loaded indicator */}
        {configLoaded && !isLoadingConfig && (
          <Alert 
            severity="success" 
            icon={<HistoryIcon />}
            sx={{ mb: 1 }}
          >
            <AlertTitle>Previous Information Loaded</AlertTitle>
            <Typography variant="body2">
              Your business information from the last session has been restored.
            </Typography>
          </Alert>
        )}
        
        {/* Business Name */}
        <FormControl fullWidth>
          <FormLabel sx={{ mb: 0.5, fontWeight: 500 }}>
            Business Name <Box component="span" sx={{ color: 'error.main' }}>*</Box>
          </FormLabel>
          <TextField
            name="businessName"
            value={formData.businessName}
            onChange={handleInputChange}
            placeholder="Enter your business name"
            size="small"
            fullWidth
          />
        </FormControl>

        {/* Industry */}
        <FormControl fullWidth>
          <FormLabel sx={{ mb: 0.5, fontWeight: 500 }}>Industry</FormLabel>
          <TextField
            name="industry"
            value={formData.industry}
            onChange={handleInputChange}
            placeholder="Enter your industry (optional)"
            size="small"
            fullWidth
          />
        </FormControl>

        {/* Description */}
        <FormControl fullWidth>
          <FormLabel sx={{ mb: 0.5, fontWeight: 500 }}>
            Business Description <Box component="span" sx={{ color: 'error.main' }}>*</Box>
          </FormLabel>
          <TextField
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Enter a description of your business"
            multiline
            rows={3}
            fullWidth
          />
        </FormControl>

        {/* Target Audience */}
        <FormControl fullWidth>
          <FormLabel sx={{ mb: 0.5, fontWeight: 500 }}>Target Audience</FormLabel>
          <TextField
            name="targetAudience"
            value={formData.targetAudience}
            onChange={handleInputChange}
            placeholder="Describe your target audience (optional)"
            multiline
            rows={2}
            fullWidth
          />
        </FormControl>

        {/* Additional Context */}
        <FormControl fullWidth>
          <FormLabel sx={{ mb: 0.5, fontWeight: 500 }}>Additional Context</FormLabel>
          <TextField
            name="additionalContext"
            value={formData.additionalContext}
            onChange={handleInputChange}
            placeholder="Any additional context that might help with analysis (optional)"
            multiline
            rows={3}
            fullWidth
          />
        </FormControl>

        {/* Brand Tokens */}
        <FormControl fullWidth>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <FormLabel sx={{ fontWeight: 500, mb: 0 }}>Brand Tokens</FormLabel>
            <Tooltip title="Generate brand tokens with AI" placement="right">
              <IconButton 
                size="small" 
                onClick={handleGenerateBrandTokens}
                disabled={isGeneratingBrandTokens || !formData.businessName.trim()}
                sx={{ 
                  padding: '2px',
                  '&:hover': { 
                    backgroundColor: 'primary.light',
                    color: 'primary.contrastText'
                  }
                }}
              >
                {isGeneratingBrandTokens ? (
                  <CircularProgress size={16} />
                ) : (
                  <Sparkles size={16} />
                )}
              </IconButton>
            </Tooltip>
          </Box>
          {isGeneratingBrandTokens && (
            <LinearProgress sx={{ mb: 0.5, height: 2 }} />
          )}
          <TextField
            name="brandTokens"
            value={formData.brandTokens}
            onChange={handleInputChange}
            placeholder="Enter brand tokens, variations, or keywords (optional)"
            size="small"
            fullWidth
            helperText="e.g., MNP, MNP Debt, mnp.ca - used to identify branded queries"
          />
        </FormControl>

        {/* Prompt Editor */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" fontWeight={500} mb={1}>
          Topical analysis
          </Typography>
          <PromptEditorV2
            module="module1_v2"
            title="Topical analysis"
            description="Customize the topical analysis based on existing topics or other preferences. PLEASE REVIEW CAREFULLY"
            onPromptChange={handlePromptChange}
          />
        </Box>

        {/* Validation Status */}
        {missingFields.length > 0 ? (
          <Alert severity="warning">
            <AlertTitle>Please provide the following required information:</AlertTitle>
            <Box component="ul" sx={{ pl: 2, mt: 1 }}>
              {missingFields.map((field, index) => (
                <Box component="li" key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Box sx={{ display: 'flex', mr: 1, color: 'warning.main' }}>
                    <AlertCircle size={16} />
                  </Box>
                  <Typography variant="body2">{field}</Typography>
                </Box>
              ))}
            </Box>
          </Alert>
        ) : (
          <Alert severity="success" icon={<CheckCircle />}>
            All required information has been provided.
          </Alert>
        )}

        {/* Spacer */}
        <Box sx={{ flex: 1 }} />

        {/* Model Selection and Submit Button */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 2 }}>
          {/* Model Selector Button */}
          <ModelSelectorButton
            selectedModel={selectedModel}
            onModelSelect={handleModelSelect}
            disabled={isSubmitting}
          />
          
          {/* Test Run Button */}
          <TestRunButton
            onTestRun={handleTestRun}
            disabled={!isComplete || isSubmitting}
            currentLimit={formData.keywordLimit || null}
          />
          
          {/* Submit Button */}
          <Button 
            onClick={handleSubmit}
            disabled={!isComplete || isSubmitting}
            variant="contained"
            color="primary"
            endIcon={!isSubmitting && <ArrowRightIcon />}
            fullWidth
            sx={{ py: 1 }}
          >
            {isSubmitting ? (
              <>
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                Processing...
              </>
            ) : (
              isComplete ? "Start AI Analysis" : "Complete Required Fields"
            )}
          </Button>
        </Box>
    </Box>
  )
}

export default InputFormContainerV2


